import { useEffect, useRef, useState } from "react";

// Accept a ref object rather than `element.current` directly. Reading
// `.current` inside the handlers (which fire after mount) means we always
// see the live element. Passing `ref.current` as a value would capture null
// on the first render and stay null until something else triggered a
// re-render — which is exactly the "toolbar is stuck" bug.
export const useDragControls = (
  elementRef: React.RefObject<HTMLDivElement | null>
) => {
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(
    () => {
      try {
        const s = localStorage.getItem("toolbar-v2-pos");
        if (!s) return null;
        const p = JSON.parse(s);
        return p.xPct != null
          ? { x: p.xPct * window.innerWidth, y: p.yPct * window.innerHeight }
          : null;
      } catch {
        return null;
      }
    }
  );

  const dragState = useRef({
    active: false,
    didDrag: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });

  const startDrag = (clientX: number, clientY: number) => {
    const element = elementRef.current;
    if (!element) return;
    const r = element.getBoundingClientRect();
    dragState.current = {
      active: true,
      didDrag: false,
      startX: clientX,
      startY: clientY,
      offsetX: clientX - r.left,
      offsetY: clientY - r.top,
    };
  };

  const onGripPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    // Keep move/up on the grip even while the cursor is over an iframe.
    // Without capture, iframe-heavy prototypes swallow the events and the
    // toolbar stalls mid-drag or stays stuck to the cursor.
    e.currentTarget.setPointerCapture(e.pointerId);
    startDrag(e.clientX, e.clientY);
  };

  const onGripDblClick = () => {
    setDragPos(null);
    localStorage.removeItem("toolbar-v2-pos");
  };

  useEffect(() => {
    const clampPos = (x: number, y: number) => {
      const element = elementRef.current;
      const p = 20,
        w = element?.offsetWidth ?? 48,
        h = element?.offsetHeight ?? 200;
      return {
        x: Math.max(p, Math.min(window.innerWidth - w - p, x)),
        y: Math.max(p, Math.min(window.innerHeight - h - p, y)),
      };
    };

    const moveDrag = (clientX: number, clientY: number) => {
      if (!dragState.current.active) return;
      if (
        Math.abs(clientX - dragState.current.startX) > 3 ||
        Math.abs(clientY - dragState.current.startY) > 3
      )
        dragState.current.didDrag = true;
      setDragPos(
        clampPos(
          clientX - dragState.current.offsetX,
          clientY - dragState.current.offsetY
        )
      );
    };

    const endDrag = () => {
      if (dragState.current.active && dragState.current.didDrag)
        setDragPos((prev) => {
          if (prev)
            try {
              localStorage.setItem(
                "toolbar-v2-pos",
                JSON.stringify({
                  xPct: prev.x / window.innerWidth,
                  yPct: prev.y / window.innerHeight,
                })
              );
            } catch {}
          return prev;
        });
      dragState.current.active = false;
    };

    const onMove = (e: PointerEvent) => moveDrag(e.clientX, e.clientY);

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, []);

  return { dragPos, onGripPointerDown, onGripDblClick };
};
